




import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {Chart} from 'highcharts-vue';
import More from 'highcharts/highcharts-more';
import Highcharts from 'highcharts';
import vue from 'vue';
import {colors} from '@/config';

More(Highcharts);

@Component({components: {Chart}})
export default class DynamicLineChart extends Vue {
  @Prop() profitloss: any;
  @Prop() balance: any;
  @Prop() cashflow: any;
  @Prop() financialRatio: any;

  @Prop() dark!: boolean;

  chartOptions: any = {
    chart: {
      type: 'line',
      backgroundColor: 'transparent',
    },

    credits: {
      enabled: false,
    },

    title: {
      text: '',
    },

    xAxis: {
      type: 'category',
      labels: {
        autoRotation: [0],
        staggerLines: 1,
        padding: 10,
        style: {
          fontSize: this.dark ? '1.25rem' : '1rem',
          textOverflow: 'none',
          whiteSpace: 'normal',
          color: this.dark ? colors.Light : colors.Black,
        },
      },
      lineColor: this.dark ? colors.Black : colors.LightGrey,
    },

    yAxis: {
      title: {
        text: '',
      },
      labels: {
        style: {
          fontSize: this.dark ? '1.25rem' : '1rem',
          color: this.dark ? colors.Light : colors.Black,
          fontWeight: this.dark ? 400 : 600,
        },
        formatter() {
          return (vue as any).options.filters.P((this as any).value);
        },
      },
      tickPixelInterval: this.dark ? 250 : 175,
      gridLineColor: this.dark ? colors.Black : colors.LightGrey,
      plotLines: [{
        color: colors.Black,
        width: this.dark ? 3 : 2,
        value: 0,
        zIndex: 2,
      }],
      softMin: 0,
      softMax: 0,
    },

    legend: {
      verticalAlign: 'top',
      align: 'right',
      enabled: true,
      itemStyle: {
        fontSize: this.dark ? '1.25rem' : '1rem',
        fontWeight: 'normal',
        color: this.dark ? colors.Light : colors.Black,
      },
      symbolWidth: 50,
      itemHoverStyle: {
        color: this.dark ? colors.Light : colors.Black,
      },
    },

    tooltip: {
      headerFormat: '',
      formatter(): any {
        return '' + (this as any).point.name + ': <b>' + (vue as any).options.filters.P((this as any).point.y) + '</b>';
      },
      style: {
        fontSize: '1.25rem',
        color: this.dark ? colors.Light : colors.Black,
      },
      backgroundColor: this.dark ? colors.Dark : colors.Light,
    },

    series: [],
  };

  @Watch('profitloss', {immediate: true, deep: true})
  profitlossChanged() {
    this.chartOptions.series = this.getData();
  }

  @Watch('balance', {immediate: true, deep: true})
  balanceChanged() {
    this.chartOptions.series = this.getData();
  }

  @Watch('cashflow', {immediate: true, deep: true})
  cashflowChanged() {
    this.chartOptions.series = this.getData();
  }

  getData() {
    switch (this.financialRatio) {
      case 'operatingMargin':
        if (!this.profitloss) {
          return [];
        }
        return [{
          name: this.getName(),
          color: this.dark ? colors.Tertiary : colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: this.dark ? colors.Dark : colors.Secondary,
            lineWidth: 2,
            lineColor: this.dark ? colors.Tertiary : colors.Primary,
            radius: this.dark ? 8 : 6,
          },
          pointPadding: 0,
          data: this.profitloss.sheets.map((p: any, i: number) => ({
            name: p.DATE_TO.slice(5, 7) === '12' ? p.DATE_TO.slice(0, 4) : p.DATE_TO.slice(0, 7),
            y: p.K / p.A,
          })).reverse(),
        }];
      case 'grossMargin':
        if (!this.profitloss) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: colors.Secondary,
            lineWidth: 2,
            lineColor: colors.Primary,
            radius: 6,
          },
          pointPadding: 0,
          lineWidth: 2,
          data: this.profitloss.sheets.map((p: any, i: number) => ({
            name: p.DATE_TO.slice(5, 7) === '12' ? p.DATE_TO.slice(0, 4) : p.DATE_TO.slice(0, 7),
            y: (p.A + p.D) / p.A,
          })).reverse(),
        }];
      case 'valueAdded':
        if (!this.profitloss) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: colors.Secondary,
            lineWidth: 2,
            lineColor: colors.Primary,
            radius: 6,
          },
          pointPadding: 0,
          data: this.profitloss.sheets.map((p: any, i: number) => ({
            name: p.DATE_TO.slice(5, 7) === '12' ? p.DATE_TO.slice(0, 4) : p.DATE_TO.slice(0, 7),
            y: (p.K - p.J - p.G) / p.A,
          })).reverse(),
        }];
      case 'returnOnCapital':
        if (!this.profitloss || !this.balance) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: colors.Secondary,
            lineWidth: 2,
            lineColor: colors.Primary,
            radius: 6,
          },
          pointPadding: 0,
          data: this.profitloss.sheets.map((p: any, i: number) => ({
            name: p.DATE_TO.slice(5, 7) === '12' ? p.DATE_TO.slice(0, 4) : p.DATE_TO.slice(0, 7),
            y: p.K / this.balance.sheets[i].S,
          })).reverse(),
        }];
      case 'returnOnEquity':
        if (!this.profitloss || !this.balance) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: colors.Secondary,
            lineWidth: 2,
            lineColor: colors.Primary,
            radius: 6,
          },
          pointPadding: 0,
          data: this.balance.sheets.map((b: any, i: number) => ({
            name: b.DATE_TO.slice(5, 7) === '12' ? b.DATE_TO.slice(0, 4) : b.DATE_TO.slice(0, 7),
            y: this.profitloss.sheets[i].N / (b.M + 0.794 * b.N),
          })).reverse(),
        }];
      case 'solidity':
        if (!this.balance) {
          return [];
        }
        return [{
          name: this.getName(),
          color: this.dark ? colors.Tertiary : colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: this.dark ? colors.Dark : colors.Secondary,
            lineWidth: 2,
            lineColor: this.dark ? colors.Tertiary : colors.Primary,
            radius: this.dark ? 8 : 6,
          },
          pointPadding: 0,
          data: this.balance.sheets.map((p: any, i: number) => ({
            name: p.DATE_TO.slice(5, 7) === '12' ? p.DATE_TO.slice(0, 4) : p.DATE_TO.slice(0, 7),
            y: (p.M + 0.794 * p.N) / p.L,
          })).reverse(),
        }];
      case 'quickRatio':
        if (!this.balance) {
          return [];
        }
        return [{
          name: this.getName(),
          color: this.dark ? colors.Tertiary : colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: this.dark ? colors.Dark : colors.Secondary,
            lineWidth: 2,
            lineColor: this.dark ? colors.Tertiary : colors.Primary,
            radius: this.dark ? 8 : 6,
          },
          pointPadding: 0,
          data: this.balance.sheets.map((p: any, i: number) => ({
            name: p.DATE_TO.slice(5, 7) === '12' ? p.DATE_TO.slice(0, 4) : p.DATE_TO.slice(0, 7),
            y: (p.K - p.F) / p.Q,
          })).reverse(),
        }];
      case 'netMargin':
        if (!this.profitloss) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: colors.Secondary,
            lineWidth: 2,
            lineColor: colors.Primary,
            radius: 6,
          },
          pointPadding: 0,
          data: this.profitloss.sheets.map((p: any, i: number) => ({
            name: p.DATE_TO.slice(5, 7) === '12' ? p.DATE_TO.slice(0, 4) : p.DATE_TO.slice(0, 7),
            y: p.R / p.A,
          })).reverse(),
        }];
      case 'operatingCashflowPercent':
        if (!this.profitloss || !this.cashflow) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: colors.Secondary,
            lineWidth: 2,
            lineColor: colors.Primary,
            radius: 6,
          },
          pointPadding: 0,
          data: this.cashflow.sheets.map((c: any, i: number) => ({
            name: c.DATE_TO.slice(5, 7) === '12' ? c.DATE_TO.slice(0, 4) : c.DATE_TO.slice(0, 7),
            y: c.OperatingCashflow / this.profitloss.sheets[i].A,
          })).reverse(),
        }];
      case 'workingCapitalPercent':
        if (!this.profitloss || !this.balance) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: colors.Secondary,
            lineWidth: 2,
            lineColor: colors.Primary,
            radius: 6,
          },
          pointPadding: 0,
          data: this.balance.sheets.map((b: any, i: number) => ({
            name: b.DATE_TO.slice(5, 7) === '12' ? b.DATE_TO.slice(0, 4) : b.DATE_TO.slice(0, 7),
            y: (b.K - b.J - b.Q + b.U) / this.profitloss.sheets[i].A,
          })).reverse(),
        }];
      case 'cashPercent':
        if (!this.profitloss || !this.balance) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: colors.Secondary,
            lineWidth: 2,
            lineColor: colors.Primary,
            radius: 6,
          },
          pointPadding: 0,
          data: this.balance.sheets.map((b: any, i: number) => ({
            name: b.DATE_TO.slice(5, 7) === '12' ? b.DATE_TO.slice(0, 4) : b.DATE_TO.slice(0, 7),
            y: b.J / this.profitloss.sheets[i].A,
          })).reverse(),
        }];
      case 'interestBearingLiabilitiesPercent':
        if (!this.profitloss || !this.balance) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: colors.Secondary,
            lineWidth: 2,
            lineColor: colors.Primary,
            radius: 6,
          },
          pointPadding: 0,
          data: this.balance.sheets.map((b: any, i: number) => ({
            name: b.DATE_TO.slice(5, 7) === '12' ? b.DATE_TO.slice(0, 4) : b.DATE_TO.slice(0, 7),
            y: ((b.U + b.P) / this.profitloss.sheets[i].A),
          })).reverse(),
        }];
      case 'investmentsCashflowPercent':
        if (!this.cashflow || !this.balance) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: colors.Secondary,
            lineWidth: 2,
            lineColor: colors.Primary,
            radius: 6,
          },
          pointPadding: 0,
          data: this.cashflow.sheets.map((c: any, i: number) => ({
            name: c.DATE_TO.slice(5, 7) === '12' ? c.DATE_TO.slice(0, 4) : c.DATE_TO.slice(0, 7),
            y: c.InvestmentsCashflow / this.profitloss.sheets[i].A,
          })).reverse(),
        }];
      case 'ownersCashflowPercent':
        if (!this.cashflow || !this.balance) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: colors.Secondary,
            lineWidth: 2,
            lineColor: colors.Primary,
            radius: 6,
          },
          pointPadding: 0,
          data: this.cashflow.sheets.map((c: any, i: number) => ({
            name: c.DATE_TO.slice(5, 7) === '12' ? c.DATE_TO.slice(0, 4) : c.DATE_TO.slice(0, 7),
            y: c.EquityWithoutNetProfitDelta / this.profitloss.sheets[i].A,
          })).reverse(),
        }];
      case 'dividentYearResultPercent':
        if (!this.cashflow || !this.balance) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: colors.Secondary,
            lineWidth: 2,
            lineColor: colors.Primary,
            radius: 6,
          },
          pointPadding: 0,
          data: this.cashflow.sheets.map((c: any, i: number) => ({
            name: c.DATE_TO.slice(5, 7) === '12' ? c.DATE_TO.slice(0, 4) : c.DATE_TO.slice(0, 7),
            // tslint:disable:max-line-length
            y: -c.EquityWithoutNetProfitDelta > 0 ? -c.EquityWithoutNetProfitDelta / this.profitloss.sheets[i + 1].R : 0,
          })).reverse(),
        }];
      case 'dividentCashPercent':
        if (!this.cashflow || !this.balance) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: colors.Secondary,
            lineWidth: 2,
            lineColor: colors.Primary,
            radius: 6,
          },
          pointPadding: 0,
          data: this.cashflow.sheets.map((c: any, i: number) => ({
            name: c.DATE_TO.slice(5, 7) === '12' ? c.DATE_TO.slice(0, 4) : c.DATE_TO.slice(0, 7),
            y: -c.EquityWithoutNetProfitDelta > 0 ? -c.EquityWithoutNetProfitDelta / this.balance.sheets[i + 1].J : 0,
          })).reverse(),
        }];
      case 'ownerCapitalCashPercent':
        if (!this.cashflow || !this.balance) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: colors.Secondary,
            lineWidth: 2,
            lineColor: colors.Primary,
            radius: 6,
          },
          pointPadding: 0,
          data: this.cashflow.sheets.map((c: any, i: number) => ({
            name: c.DATE_TO.slice(5, 7) === '12' ? c.DATE_TO.slice(0, 4) : c.DATE_TO.slice(0, 7),
            y: c.EquityWithoutNetProfitDelta + c.SubscribedCapitalUnpaidDelta > 0 ? (c.EquityWithoutNetProfitDelta + c.SubscribedCapitalUnpaidDelta) / this.balance.sheets[i].J : 0,
          })).reverse(),
        }];
      case 'growth':
        if (!this.profitloss) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Secondary,
          dashStyle: 'dash',
          marker: {
            fillColor: colors.Secondary,
            lineWidth: 2,
            lineColor: colors.Primary,
            radius: 6,
          },
          pointPadding: 0,
          data: this.profitloss.sheets.map((p: any, i: number) => ({
            name: p.DATE_TO.slice(5, 7) === '12' ? p.DATE_TO.slice(0, 4) : p.DATE_TO.slice(0, 7),
            y: i + 1 < this.profitloss.count ? ((p.A * (12 / p.MONTHS)) / (this.profitloss.sheets[i + 1].A) * ( 12 / this.profitloss.sheets[i + 1].MONTHS) ) - 1 : null,
          })).reverse(),
        }];
      }
  }

  getName() {
    switch (this.financialRatio) {
      case 'operatingMargin':
        return Vue.prototype.$t('Rörelsemarginal');
      case 'grossMargin':
        return Vue.prototype.$t('Bruttomarginal');
      case 'valueAdded':
        return Vue.prototype.$t('Förädlingsgrad');
      case 'returnOnCapital':
        return Vue.prototype.$t('Avkastning på totalt kapital');
      case 'returnOnEquity':
        return Vue.prototype.$t('Avkastning på eget kapital');
      case 'solidity':
        return Vue.prototype.$t('Soliditet');
      case 'quickRatio':
        return Vue.prototype.$t('Kassalikviditet');
      case 'netMargin':
        return Vue.prototype.$t('Nettomarginal');
      case 'operatingCashflowPercent':
        return Vue.prototype.$t('Operativa kassaflöden i % av nettoomsättningen');
      case 'workingCapitalPercent':
        return Vue.prototype.$t('Bindning i rörelsekapital (exkl. kassa & bank) i % av nettoomsättningen');
      case 'cashPercent':
        return Vue.prototype.$t('Kassa & bank som % av nettoomsättning');
      case 'interestBearingLiabilitiesPercent':
        return Vue.prototype.$t('Räntebärande skulder som % av nettoomsättning');
      case 'investmentsCashflowPercent':
        return Vue.prototype.$t('Investeringar som % av nettoomsättning');
      case 'ownersCashflowPercent':
        return Vue.prototype.$t('Ägarkapital som % av nettoomsättning');
      case 'dividentYearResultPercent':
        return Vue.prototype.$t('Utdelade medel som andel av föregående periods Årets resultat');
      case 'dividentCashPercent':
        return Vue.prototype.$t('Utdelade medel som andel av föregående periods kassa & bank');
      case 'ownerCapitalCashPercent':
        return Vue.prototype.$t('Under året tillfört ägarkapital som andel av UB för kassa & bank');
      case 'growth':
        return Vue.prototype.$t('Årlig tillväxt');
    }
  }
}
